import { useState, useEffect, useCallback } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { ShieldCheck, CreditCard, CheckCircle2, XCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const PayPage = () => {
  const { ref } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/payments/invoice/${encodeURIComponent(ref)}`)
      .then(({ data }) => setInvoice(data))
      .catch(() => setError("Invoice not found. Check the invoice number and try again."));
  }, [ref]);

  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/payments/invoice-checkout`, {
        ref,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout. Please call 470-692-2559.");
      setBusy(false);
    }
  };

  return (
    <div className="epic-app admin-page" data-testid="pay-page">
      <Toaster position="top-center" theme="dark" />
      <div className="form-container pay-card">
        <img src="/assets/logo.webp" alt="Epic Expectations logo" className="footer-logo" />
        <h2 className="neon-text-blue" data-testid="pay-heading">SECURE INVOICE PAYMENT</h2>
        <p className="admin-sub">EPIC EXPECTATIONS&trade; · Card payments by Stripe</p>
        {error && <p className="admin-error" data-testid="pay-error">{error}</p>}
        {invoice && (
          <div data-testid="pay-invoice">
            <p className="pay-line"><strong>Invoice:</strong> {invoice.doc_number}</p>
            <p className="pay-line"><strong>Client:</strong> {invoice.client_name}</p>
            <p className="pay-line"><strong>Date:</strong> {invoice.doc_date}</p>
            <div className="pay-items">
              {invoice.line_items.map((li, i) => (
                <div className="pay-item" key={i}>
                  <span>{li.description}{li.qty ? ` (${li.qty})` : ""}</span>
                  <span>${li.amount.toFixed(2)}</span>
                </div>
              ))}
              <div className="pay-item pay-sub"><span>Subtotal</span><span>${invoice.subtotal.toFixed(2)}</span></div>
              <div className="pay-item pay-sub"><span>Taxes</span><span>${invoice.taxes.toFixed(2)}</span></div>
              <div className="pay-item pay-total"><span>Total</span><span>${invoice.total.toFixed(2)}</span></div>
              {invoice.amount_paid > 0 && <div className="pay-item pay-sub"><span>Paid</span><span>-${invoice.amount_paid.toFixed(2)}</span></div>}
              <div className="pay-item pay-balance"><span>Balance Due</span><span>${invoice.balance_due.toFixed(2)}</span></div>
            </div>
            {invoice.balance_due <= 0 ? (
              <p className="neon-text-green pay-paid" data-testid="pay-paid-message"><CheckCircle2 size={18} /> PAID IN FULL — MISSION ACCOMPLISHED</p>
            ) : invoice.status !== "issued" ? (
              <p className="admin-sub" data-testid="pay-not-issued">
                This invoice unlocks for payment when your job is complete. The Epic Squad will send your pay link the moment the work is done — questions? Call <a href="tel:4706922559" className="neon-text-green">470-692-2559</a>.
              </p>
            ) : (
              <button className="submit-btn pay-btn" onClick={pay} disabled={busy} data-testid="pay-now-button">
                <CreditCard size={18} /> {busy ? "REDIRECTING..." : `PAY $${invoice.balance_due.toFixed(2)} SECURELY`}
              </button>
            )}
          </div>
        )}
        <p className="form-alt"><ShieldCheck size={14} /> Payments processed securely by Stripe. We never see your card details.</p>
        <p className="form-alt"><Link to="/" className="neon-text-blue" data-testid="pay-back-link">Back to epicexpectation.com</Link></p>
      </div>
    </div>
  );
};

export const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("checking");
  const [docNumber, setDocNumber] = useState("");

  const poll = useCallback(async () => {
    for (let i = 0; i < 10; i++) {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setDocNumber(data.doc_number || "");
          setStatus("paid");
          return;
        }
      } catch (e) {
        break;
      }
      await new Promise((r) => setTimeout(r, 2000));
    }
    setStatus("pending");
  }, [sessionId]);

  useEffect(() => {
    if (sessionId) poll();
    else setStatus("pending");
  }, [sessionId, poll]);

  return (
    <div className="epic-app admin-page" data-testid="payment-success-page">
      <div className="form-container pay-card">
        <CheckCircle2 size={56} className="admin-shield pay-success-icon" />
        <h2 className="neon-text-green" data-testid="payment-success-heading">
          {status === "paid" ? "PAYMENT RECEIVED" : "CONFIRMING PAYMENT..."}
        </h2>
        {status === "paid" && (
          <p className="admin-sub" data-testid="payment-success-detail">
            MISSION ACCOMPLISHED{docNumber ? ` — Invoice ${docNumber} is paid in full` : ""}. The Epic Squad salutes you.
          </p>
        )}
        {status === "pending" && (
          <p className="admin-sub" data-testid="payment-pending-detail">Still confirming with Stripe — check back in a moment or call 470-692-2559.</p>
        )}
        <p className="form-alt"><Link to="/" className="neon-text-blue" data-testid="payment-success-home-link">Back to epicexpectation.com</Link></p>
      </div>
    </div>
  );
};

export const PaymentCancel = () => (
  <div className="epic-app admin-page" data-testid="payment-cancel-page">
    <div className="form-container pay-card">
      <XCircle size={56} className="pay-cancel-icon" />
      <h2 className="neon-text-pink" data-testid="payment-cancel-heading">PAYMENT CANCELLED</h2>
      <p className="admin-sub">No charge was made. Your invoice is still waiting whenever you&apos;re ready.</p>
      <p className="form-alt"><Link to="/" className="neon-text-blue" data-testid="payment-cancel-home-link">Back to epicexpectation.com</Link></p>
    </div>
  </div>
);
